/**
 * The board's calendar holds, per role.
 *
 * Kept OUT of the board snapshot on purpose. The board is a Lever pull that is
 * cached and only refreshed on demand, but a hold changes the moment anyone
 * blocks, offers or releases a slot — and a stale overview is worse than no
 * overview, because the recruiter releases from it. So holds are read straight
 * from Firestore whenever the set of roles changes, and every mutating call
 * returns the role's new list, which is applied directly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../services/taActionBoardApi';
import type { SlotHold } from '../../types/taActionBoard';

export interface SlotHoldsState {
  /** postingId → holds, chronological. A missing role means "not loaded yet". */
  byPosting: Record<string, SlotHold[]>;
  /** postingId → count of holds left over from the previous, unaddressable shape */
  legacy: Record<string, number>;
  /** Apply the list a mutating call returned for one role */
  apply: (postingId: string, holds: SlotHold[]) => void;
  /** Drop the legacy counter for a role once its old holds have been drained */
  clearLegacy: (postingId: string) => void;
  reload: () => void;
}

export function useSlotHolds(postingIds: string[]): SlotHoldsState {
  const [byPosting, setByPosting] = useState<Record<string, SlotHold[]>>({});
  const [legacy, setLegacy] = useState<Record<string, number>>({});
  // A stable key, so re-rendering with a fresh array does not refetch.
  const key = useMemo(() => [...new Set(postingIds.filter(Boolean))].sort().join(','), [postingIds]);

  const load = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    api
      .fetchHolds(ids)
      .then((r) => {
        // Merged rather than replaced: switching boards must not blank the holds
        // of a role the recruiter comes back to a moment later.
        setByPosting((prev) => ({ ...prev, ...(r.holds || {}) }));
        setLegacy((prev) => ({ ...prev, ...(r.legacy || {}) }));
      })
      // Silent: the overview simply stays empty, and every hold action reports
      // its own outcome. A toast here would fire on every board switch offline.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load(key ? key.split(',') : []);
  }, [key, load]);

  const apply = useCallback((postingId: string, holds: SlotHold[]) => {
    setByPosting((prev) => ({ ...prev, [postingId]: holds || [] }));
  }, []);

  const clearLegacy = useCallback((postingId: string) => {
    setLegacy((prev) => {
      if (!(postingId in prev)) return prev;
      const next = { ...prev };
      delete next[postingId];
      return next;
    });
  }, []);

  const reload = useCallback(() => load(key ? key.split(',') : []), [key, load]);

  return { byPosting, legacy, apply, clearLegacy, reload };
}
